import React, { useState, useEffect, useCallback } from 'react';
import { ExerciseDiscussion, CreateCommentRequest } from '../../types/grammarPractice';
import { getExerciseDiscussion, addComment } from '../../api/grammarPracticeApi';
import CommentItem from './CommentItem';
import AddCommentForm from './AddCommentForm';

interface DiscussionSectionProps {
  exerciseId: number;
}

const DiscussionSection: React.FC<DiscussionSectionProps> = ({ exerciseId }) => {
  const [discussion, setDiscussion] = useState<ExerciseDiscussion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDiscussion = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getExerciseDiscussion(exerciseId);
      setDiscussion(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load discussion');
      console.error('Error loading discussion:', err);
    } finally {
      setLoading(false);
    }
  }, [exerciseId]);

  useEffect(() => {
    loadDiscussion();
  }, [loadDiscussion]);

  const handleAddComment = async (request: CreateCommentRequest) => {
    try {
      const newComment = await addComment(exerciseId, request);
      // Reload discussion to get updated comments
      await loadDiscussion();
      return newComment;
    } catch (err) {
      throw err;
    }
  };

  const handleCommentUpdated = () => {
    // Reload discussion when a comment is updated or deleted
    loadDiscussion();
  };

  if (loading) {
    return (
      <div className="bg-base-200 rounded-lg p-6">
        <div className="flex justify-center">
          <span className="loading loading-spinner loading-md"></span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-base-200 rounded-lg p-6">
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!discussion) {
    return null;
  }

  // Get only top-level comments (no parent)
  const topLevelComments = discussion.comments.filter(c => !c.parent_comment);

  return (
    <div className="bg-base-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold">
          Discussion ({discussion.comment_count})
        </h3>
      </div>

      <div className="text-sm text-base-content/70">
        <p>Use this section to:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Report issues with this exercise</li>
          <li>Ask theoretical questions about the grammar</li>
          <li>Discuss the concept with other learners</li>
        </ul>
      </div>

      {/* Add comment form */}
      <AddCommentForm onSubmit={handleAddComment} />

      {/* Comments list */}
      {topLevelComments.length > 0 ? (
        <div className="space-y-4">
          {topLevelComments.map(comment => (
            <CommentItem
              key={comment.comment_id}
              comment={comment}
              exerciseId={exerciseId}
              onCommentUpdated={handleCommentUpdated}
            />
          ))}
        </div>
      ) : (
        <div className="text-center text-base-content/50 py-8">
          No comments yet. Be the first to start the discussion!
        </div>
      )}
    </div>
  );
};

export default DiscussionSection;
